import type { AccentName } from './api';

/**
 * Editorial accent system. Each accent contributes:
 *   strip  — solid bg for the thick left-edge bar on tiles
 *   glyph  — text colour for the icon character
 *   chip   — tag pill (bg + text) for compact status
 *   dot    — bg for an inline status dot
 *   tint   — soft tinted radial colour used behind the icon block
 *   hex    — raw hex for inline SVG / canvas use
 *
 * Tailwind classes are listed explicitly so the JIT keeps them.
 */
export const ACCENT_STYLES: Record<
  AccentName,
  {
    strip: string;
    glyph: string;
    chip: string;
    dot: string;
    tint: string;
    hex: string;
  }
> = {
  slate: {
    strip: 'bg-cream-700',
    glyph: 'text-cream',
    chip: 'bg-cream-700/15 text-cream-700',
    dot: 'bg-cream-700',
    tint: 'from-cream-700/10',
    hex: '#D7CFC0',
  },
  sky: {
    strip: 'bg-sky-400',
    glyph: 'text-sky-300',
    chip: 'bg-sky-400/15 text-sky-300',
    dot: 'bg-sky-400',
    tint: 'from-sky-400/15',
    hex: '#38BDF8',
  },
  amber: {
    strip: 'bg-amber-400',
    glyph: 'text-amber-300',
    chip: 'bg-amber-400/15 text-amber-300',
    dot: 'bg-amber-400',
    tint: 'from-amber-400/15',
    hex: '#FBBF24',
  },
  rose: {
    strip: 'bg-rose-400',
    glyph: 'text-rose-300',
    chip: 'bg-rose-400/15 text-rose-300',
    dot: 'bg-rose-400',
    tint: 'from-rose-400/15',
    hex: '#FB7185',
  },
  emerald: {
    strip: 'bg-emerald-400',
    glyph: 'text-emerald-300',
    chip: 'bg-emerald-400/15 text-emerald-300',
    dot: 'bg-emerald-400',
    tint: 'from-emerald-400/15',
    hex: '#34D399',
  },
  violet: {
    strip: 'bg-violet-400',
    glyph: 'text-violet-300',
    chip: 'bg-violet-400/15 text-violet-300',
    dot: 'bg-violet-400',
    tint: 'from-violet-400/15',
    hex: '#A78BFA',
  },
  indigo: {
    strip: 'bg-indigo-400',
    glyph: 'text-indigo-300',
    chip: 'bg-indigo-400/15 text-indigo-300',
    dot: 'bg-indigo-400',
    tint: 'from-indigo-400/15',
    hex: '#818CF8',
  },
  cyan: {
    strip: 'bg-cyan-400',
    glyph: 'text-cyan-300',
    chip: 'bg-cyan-400/15 text-cyan-300',
    dot: 'bg-cyan-400',
    tint: 'from-cyan-400/15',
    hex: '#22D3EE',
  },
  pink: {
    strip: 'bg-pink-400',
    glyph: 'text-pink-300',
    chip: 'bg-pink-400/15 text-pink-300',
    dot: 'bg-pink-400',
    tint: 'from-pink-400/15',
    hex: '#F472B6',
  },
  lime: {
    strip: 'bg-lime-400',
    glyph: 'text-lime-300',
    chip: 'bg-lime-400/15 text-lime-300',
    dot: 'bg-lime-400',
    tint: 'from-lime-400/15',
    hex: '#A3E635',
  },
  orange: {
    strip: 'bg-orange-400',
    glyph: 'text-orange-300',
    chip: 'bg-orange-400/15 text-orange-300',
    dot: 'bg-orange-400',
    tint: 'from-orange-400/15',
    hex: '#FB923C',
  },
};
